import React from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import AddIcon from '@atlaskit/icon/core/add';
import { Box, Inline, Pressable, type PressableProps, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useFireAnalyticEvent } from '../analytics';

import { messages } from './messages';

const styles = cssMap({
	createButton: {
		position: 'relative',
		width: '100%',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		backgroundColor: token('elevation.surface.overlay'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-visible': {
			outline: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&::before': {
			content: '""',
			position: 'absolute',
			left: token('space.0'),
			top: token('space.0'),
			bottom: token('space.0'),
			width: '2px',
			borderLeft: `${token('border.width.focused')} solid ${token('color.border.selected')}`,
			visibility: 'hidden',
		},
		'&:hover::before': {
			visibility: 'visible',
		},
		'&:focus::before': {
			visibility: 'visible',
		},
		'&:active::before': {
			visibility: 'visible',
		},
	},
	iconWrapper: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
	},
});

export type CreateProjectButtonProps = PressableProps;

export const CreateProjectButton = ({
	onClick,
	...props
}: CreateProjectButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useFireAnalyticEvent();

	const handleClick: PressableProps['onClick'] = (...onClickArgs) => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'townsquareProjectPickerCreateProjectButton',
		});

		onClick?.(...onClickArgs);
	};

	return (
		<Pressable xcss={styles.createButton} onClick={handleClick} {...props}>
			<Inline space="space.100" alignBlock="center">
				<Box xcss={styles.iconWrapper}>
					<AddIcon label="" spacing="none" color="var(--ds-icon)" />
				</Box>
				<Text>{formatMessage(messages.createProjectButtonLabel)}</Text>
			</Inline>
		</Pressable>
	);
};
